import { useMutation, useQueryClient, useSuspenseQueries } from '@tanstack/react-query'
import { type ChangeEvent, useEffect, useMemo } from 'react'
import { type ControllerRenderProps, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { MB } from '@/lib/constants/file'
import { updateCompanyMutation } from '@/lib/queries/business/v1/company/mutations'
import { getCompanyQuery } from '@/lib/queries/business/v1/company/queries'
import { updateUserMutation } from '@/lib/queries/business/v1/user/mutations'
import { getMembersQuery } from '@/lib/queries/business/v1/user/queries'
import { getUserQuery } from '@/lib/queries/business/v2/user/queries'

export const useSettingsContent = () => {
  const queryClient = useQueryClient()

  const [getUserQueryResult, getCompanyQueryResult] = useSuspenseQueries({
    queries: [getUserQuery, getCompanyQuery],
  })
  const dataUser = getUserQueryResult.data
  const dataCompany = getCompanyQueryResult.data

  const form = useForm<{
    userName: string
    companyName: string
    companyLogo?: File
    companyDefaultCaseVisibility: boolean
    companyDefaultCaseInactivation: boolean
  }>({
    mode: 'onSubmit',
    defaultValues: {
      userName: dataUser?.user.name,
      companyName: dataCompany?.name,
      companyLogo: undefined,
      companyDefaultCaseVisibility: dataCompany?.case_visibility,
      companyDefaultCaseInactivation: dataCompany?.enableArchive,
    },
  })
  const { errors } = form.formState

  const userInputImage = useWatch({
    control: form.control,
    name: 'companyLogo',
  })
  const userInputImageSrc = useMemo(() => {
    if (!userInputImage) {
      return null
    }
    return URL.createObjectURL(userInputImage)
  }, [userInputImage])

  const { mutateAsync: updateCompanySettings, isPending: isPendingUpdateCompanySettings } =
    useMutation(updateCompanyMutation)

  const { mutateAsync: updateUserSettings, isPending: isPendingUpdateUserSettings } = useMutation(updateUserMutation)

  const isDisabledSaveButton =
    !!errors.userName ||
    !!errors.companyName ||
    !!errors.companyLogo ||
    isPendingUpdateCompanySettings ||
    isPendingUpdateUserSettings

  const handleSubmit = form.handleSubmit(
    async ({ userName, companyName, companyLogo, companyDefaultCaseVisibility, companyDefaultCaseInactivation }) => {
      try {
        await Promise.all([
          updateUserSettings({ name: userName }),
          updateCompanySettings({
            name: companyName,
            file: companyLogo,
            case_visibility: companyDefaultCaseVisibility,
            enableArchive: companyDefaultCaseInactivation,
          }),
        ])
        await Promise.all([
          queryClient.invalidateQueries(getMembersQuery).catch(),
          queryClient.invalidateQueries(getUserQuery).catch(),
          queryClient.invalidateQueries(getCompanyQuery).catch(),
        ])
        toast.success('Settings updated.')
      } catch (err) {
        toast.error('Update failed. Please try again later.')
      }
    }
  )

  const handleChangeFileInput = (
    $event: ChangeEvent<HTMLInputElement>,
    onChange: ControllerRenderProps['onChange']
  ) => {
    const file = $event.target.files?.[0]
    const fileSize = file?.size

    /** when file exceeds 10 MB in size */
    if (fileSize && fileSize >= 10 * MB) {
      form.setError('companyLogo', {
        message: 'File size exceeds the 10MB limit. Please try again with a smaller file.',
      })
      return
    }

    form.clearErrors('companyLogo')
    onChange(file)
  }

  useEffect(() => {
    return () => {
      if (userInputImageSrc) {
        URL.revokeObjectURL(userInputImageSrc)
      }
    }
  }, [userInputImageSrc])

  return {
    form,
    userInputImageSrc,
    dataUser,
    dataCompany,
    errors,
    isDisabledSaveButton,
    handleSubmit,
    handleChangeFileInput,
  }
}
